import { useState, useEffect, useRef } from 'react';

interface UseServiceWorkerUpdateResult {
  isUpdateAvailable: boolean;
  isUpdating: boolean;
  applyUpdate: () => void;
}

export function useServiceWorkerUpdate(): UseServiceWorkerUpdateResult {
  const [isUpdateAvailable, setIsUpdateAvailable] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const waitingSWRef = useRef<ServiceWorker | null>(null);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    let intervalId: ReturnType<typeof setInterval> | null = null;

    const handleWaiting = (sw: ServiceWorker) => {
      waitingSWRef.current = sw;
      setIsUpdateAvailable(true);
    };

    const handleUpdateFound = (registration: ServiceWorkerRegistration) => {
      const newSW = registration.installing;
      if (!newSW) return;

      newSW.addEventListener('statechange', () => {
        if (newSW.state === 'installed' && navigator.serviceWorker.controller) {
          handleWaiting(newSW);
        }
      });
    };

    // navigator.serviceWorker.ready 回傳既有 registration，避免重複 register
    navigator.serviceWorker.ready
      .then((registration) => {
        if (registration.waiting) {
          handleWaiting(registration.waiting);
        }

        const onUpdateFound = () => handleUpdateFound(registration);
        registration.addEventListener('updatefound', onUpdateFound);

        // 每 60 分鐘主動 check 一次（針對長時間停留同一頁的 PWA 情境）
        intervalId = setInterval(() => {
          registration.update().catch(() => {
            // 網路問題時靜默忽略，下次 interval 再試
          });
        }, 60 * 60 * 1000);

        return () => {
          registration.removeEventListener('updatefound', onUpdateFound);
        };
      })
      .catch(() => {
        // SW 不可用時靜默忽略（開發環境）
      });

    return () => {
      if (intervalId !== null) clearInterval(intervalId);
    };
  }, []);

  const applyUpdate = () => {
    const sw = waitingSWRef.current;
    if (!sw) return;

    setIsUpdating(true);
    sw.postMessage({ type: 'SKIP_WAITING' });

    navigator.serviceWorker.addEventListener('controllerchange', () => {
      window.location.reload();
    }, { once: true });
  };

  return { isUpdateAvailable, isUpdating, applyUpdate };
}
